import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import ContentEditor from "aldoo-ra/CMS/content-editor"
import { showNotification } from "aldoo-ra/Notification"
import { GetParams, GetParam, CreateParam, UpdateParam } from "../../ParamIO/api"
import StoreSettingsContentType from "aldoo-ra/CMSModels/StoreSettings"
import PermissionCheck from "aldoo-ra/PermissionChecker"

// Maps field IDs to their paramIO addresses
const FIELD_ADDRESS_MAP = {
  STRIPE_PUBLISHABLE_KEY: "STRIPE.STRIPE_PUBLISHABLE_KEY",
  STRIPE_WEBHOOK_SECRET: "STRIPE.STRIPE_WEBHOOK_SECRET",
  STRIPE_SECRET_KEY: "STRIPE.STRIPE_SECRET_KEY",
  PURCHASE_CONFIRMATION_PLATFORMS: "PURCHASE_CONFIRMATION_PLATFORMS"
}

const FIELD_DESCRIPTIONS = {
  STRIPE_PUBLISHABLE_KEY: "Stripe publishable key for client-side integration",
  STRIPE_WEBHOOK_SECRET: "Secret used to verify Stripe webhook signatures",
  STRIPE_SECRET_KEY: "Stripe secret key for server-side API calls",
  PURCHASE_CONFIRMATION_PLATFORMS: "Platforms that should receive purchase confirmation notifications"
}

// Convert string array ["Android", "iOS"] to object array [{name: "Android"}, {name: "iOS"}]
const stringsToObjects = (arr) => {
  if (!Array.isArray(arr)) return []
  return arr.map(item => typeof item === "string" ? { name: item } : item)
}

// Convert object array [{name: "Android"}] back to string array ["Android"]
const objectsToStrings = (arr) => {
  if (!Array.isArray(arr)) return []
  return arr.map(item => (typeof item === "object" && item?.name) ? item.name : item)
}

const StoreSettings = () => {
  const [fieldValues, setFieldValues] = useState({})
  const [loading, setLoading] = useState(true)
  const [originalValues, setOriginalValues] = useState({})

  const loadCurrentSettings = async () => {
    const settings = {
      STRIPE_PUBLISHABLE_KEY: "",
      STRIPE_WEBHOOK_SECRET: "",
      STRIPE_SECRET_KEY: "",
      PURCHASE_CONFIRMATION_PLATFORMS: []
    }

    for (const [fieldId, address] of Object.entries(FIELD_ADDRESS_MAP)) {
      try {
        const param = await GetParams({ match: { address } }).catch(() => null)
        if (!param?.data?.[0]) continue

        const value = param.data[0].value

        if (fieldId === "PURCHASE_CONFIRMATION_PLATFORMS") {
          settings[fieldId] = stringsToObjects(value || [])
        } else {
          settings[fieldId] = value === "none" ? "" : (value || "")
        }
      } catch (error) {
        console.warn(`Failed to load ${address}:`, error)
      }
    }

    return settings
  }

  const handleSave = async (values) => {
    const savePromises = []

    for (const [fieldId, address] of Object.entries(FIELD_ADDRESS_MAP)) {
      const value = values[fieldId]
      const originalValue = originalValues[fieldId]

      let processedValue = value
      let processedOriginalValue = originalValue

      if (fieldId === "PURCHASE_CONFIRMATION_PLATFORMS") {
        processedValue = objectsToStrings(value)
        processedOriginalValue = objectsToStrings(originalValue)
        if (JSON.stringify(processedValue) === JSON.stringify(processedOriginalValue)) continue
      } else {
        if (processedValue === processedOriginalValue) continue
      }

      const storeValue = Array.isArray(processedValue) ? processedValue : (processedValue || "none")
      const description = FIELD_DESCRIPTIONS[fieldId] || "Store configuration setting"

      const savePromise = GetParam(address)
        .then(existing => {
          const paramData = { address, value: storeValue, description }
          if (existing && existing.result !== false) return UpdateParam(paramData)
          return CreateParam(paramData)
        })
        .catch(() => CreateParam({ address, value: storeValue, description }))

      savePromises.push(savePromise)
    }

    if (savePromises.length === 0) return false

    try {
      await Promise.all(savePromises)
      setOriginalValues({ ...values })
      setFieldValues({ ...values })
      showNotification({
        message: `Store settings saved successfully (${savePromises.length} changes)`,
        className: "bg-admin_success text-admin_text_inv"
      })
      return true
    } catch (error) {
      console.error("Failed to save store settings:", error)
      showNotification({
        message: "Failed to save store settings",
        className: "bg-admin_error text-admin_text_inv"
      })
      return false
    }
  }

  useEffect(() => {
    const init = async () => {
      setLoading(true)
      try {
        const currentSettings = await loadCurrentSettings()
        setFieldValues(currentSettings)
        setOriginalValues(currentSettings)
      } catch (error) {
        console.error("Failed to initialize store settings:", error)
        showNotification({
          message: "Failed to load store settings",
          className: "bg-admin_error text-admin_text_inv"
        })
      } finally {
        setLoading(false)
      }
    }

    init()
  }, [])

  if (loading) {
    return (
      <div className="p-6">
        <Typography variant="admin_body">Loading store settings...</Typography>
      </div>
    )
  }

  return (
    <div className="p-6">
      <PermissionCheck permission="role:store" />
      <div className="max-w-6xl mx-auto">
        <div className="bg-paper rounded-lg border border-admin_bordered">
          <ContentEditor
            title="Store Configuration"
            contentType={StoreSettingsContentType}
            dataItem={{ content: fieldValues }}
            fieldValues={fieldValues}
            onSave={handleSave}
            onError={(errors) => {
              showNotification({
                message: Object.values(errors).find((e) => e) || "Validation error",
                className: "bg-admin_error text-white"
              })
            }}
            variant="default"
            showCopyPaste={false}
            showExport={false}
            autoSave={true}
            autoSaveDelay={500}
          />
        </div>
      </div>
    </div>
  )
}

export default StoreSettings
